import { filterBy, orderBy } from "@progress/kendo-data-query";
import axios from "axios";
import * as math from "mathjs";
import {
  allDatasets,
  allTemplates,
  isAdmin,
  userData,
  userEmail,
  userEntries,
} from "../Common/Store/CommonSignals";
import {
  entryValues,
  formErrors,
  galleryValues,
  initialCommentsLengths,
  resyncGalleryDifferences,
  selectedStep,
  showAtlasMenu,
  steps,
  template,
} from "../Entry/Manage/EditEntrySignals";
import customMath from "../Mathjs/CustomMathjsInstance";

export function isApprovalModeEntry(selectedTemplate, templateId) {
  if (
    selectedTemplate.owner.toLowerCase() === userEmail.value.toLowerCase() ||
    selectedTemplate.coOwners.some(
      (coOwner) => coOwner.user.toLowerCase() === userEmail.value.toLowerCase() || isAdmin.value
    )
  ) {
    return false;
  }

  const mocTemplates = allDatasets.value?.find(
    (dataset) => dataset.id === "64f6157c-3f87-4c84-8231-f93cc1251721"
  );

  if (mocTemplates.values?.some((item) => item.value === templateId)) {
    if (entryValues.value["Change Owner"]) {
      return checkEntryOwner("Change Owner", entryValues.value);
    }
    if (entryValues.value["MOC Coordinator"]) {
      return checkEntryOwner("MOC Coordinator", entryValues.value);
    }
  }

  return false;
}

function checkEntryOwner(ownerField, entryValues) {
  const entryOwnerArray = JSON.parse(entryValues[ownerField].toLowerCase());
  const isOwner = (element) => element.email.toLowerCase() === userEmail.value.toLowerCase();
  return !entryOwnerArray.some(isOwner);
}

export function resetChildValueOnChange(template, fieldName, formRenderProps) {
  template?.sections?.forEach((section) => {
    section.fields?.forEach((field) => {
      if (field?.properties?.["ParentFieldName"] === fieldName) {
        field.value = null;
        formRenderProps?.onChange(field?.fieldName, {
          value: null,
        });
      }
    });
  });
}

export const evaluateRowFormatting = (rowData, conditionalRowFormattingRules) => {
  if (!conditionalRowFormattingRules || !rowData) return null;

  const rowStyles = {};

  for (const rule of conditionalRowFormattingRules) {
    try {
      const parser = math.parser();
      Object.entries(rowData).forEach(([key, value]) => {
        parser.set(key.replace(/\s/g, ""), value);
      });
      const result = parser.evaluate(rule.rule.replaceAll("\r", "").replaceAll("\n", ""));

      if (result) {
        Object.assign(rowStyles, rule.style);
        if (rule.stopProcessing) {
          break;
        }
      }
    } catch (e) {
      console.error("Error evaluating row formatting rule:", e);
    }
  }

  return Object.keys(rowStyles).length > 0 ? rowStyles : null;
};

export const evaluateCondition = (rules, value, fieldName) => {
  if (!rules) return null;
  if (value === null || value === undefined) {
    value = "";
  }

  try {
    let style = null;
    for (const rule of rules) {
      const parser = math.parser();
      if (entryValues?.value) {
        Object.entries(entryValues.value).forEach(([key, val]) => {
          parser.set(key.replace(/\s/g, ""), val);
        });
      }
      // Ensure current field value is available in parser and not null or undefined
      if (fieldName) {
        parser.set(fieldName.replace(/\s/g, ""), value);
      }

      let result = parser.evaluate(rule.rule.replaceAll("\r", "").replaceAll("\n", ""));
      if (result) {
        style = rule.style;
      }
      if (rule.stopProcessing && result) {
        break;
      }
    }
    return style;
  } catch (e) {
    console.log("error", e);
    return null;
  }
};

export const evaluateConditionViewEntry = (rules, values) => {
  if (!rules) return null;
  if (!values) return null;

  try {
    let style = null;
    for (const rule of rules) {
      const parser = math.parser();

      Object.entries(values).forEach(([key, val]) => {
        parser.set(key.replace(/\s/g, ""), val);
      });

      let result = parser.evaluate(rule.rule.replaceAll("\r", "").replaceAll("\n", ""));
      if (result) {
        style = rule.style;
      }
      if (rule.stopProcessing && result) {
        break;
      }
    }
    return style;
  } catch (e) {
    console.log("error", e);
    return null;
  }
};

export const setStyle = (element, selector, conditionalFormattingRules, value, fieldName) => {
  if (element) {
    const inputElement = element.querySelector(selector);
    if (inputElement) {
      // Clear previous conditional formatting styles
      inputElement.style.removeProperty("background-color");
      inputElement.style.removeProperty("color");
      const style = evaluateCondition(conditionalFormattingRules, value, fieldName);
      if (style) {
        // Apply styles with 'important' priority to override dark mode CSS
        if (style.backgroundColor) {
          inputElement.style.setProperty("background-color", style.backgroundColor, "important");
        }
        if (style.color) {
          inputElement.style.setProperty("color", style.color, "important");
        }
      }
    }
  }
};

export const setStyleViewEntry = (element, selector, conditionalFormattingRules, values) => {
  if (element) {
    const inputElement = element.querySelector(selector);
    if (inputElement) {
      // Clear previous conditional formatting styles
      inputElement.style.removeProperty("background-color");
      inputElement.style.removeProperty("color");
      const style = evaluateConditionViewEntry(conditionalFormattingRules, values);
      if (style) {
        // Apply styles with 'important' priority to override dark mode CSS
        if (style.backgroundColor) {
          inputElement.style.setProperty("background-color", style.backgroundColor, "important");
        }
        if (style.color) {
          inputElement.style.setProperty("color", style.color, "important");
        }
      }
    }
  }
};

let isInvalidStage = false;

export const validateStageStep = () => {
  isInvalidStage = false;
  let visibilityResult = {};
  return new Promise((resolve) => {
    template.value.sections
      .filter(
        (section) =>
          section.properties.StageStep &&
          section.properties.StageStep.text === steps.value[selectedStep].label
      )
      .forEach((section) => {
        visibilityResult = Invisible({
          VisibilityRules: section.properties?.VisibilityRules,
          parameterValues: section.properties?.ParameterValues,
        });

        if (visibilityResult.invisible) {
          const tempFormErrors = { ...formErrors.value };
          tempFormErrors[section.sectionName] = [];
          formErrors.value = tempFormErrors;
          return;
        }
        section.fields.forEach((field) => {
          visibilityResult = Invisible({
            VisibilityRules: field.properties?.VisibilityRules,
            parameterValues: field.properties?.ParameterValues,
          });

          if (visibilityResult.invisible) return;

          ensureSectionErrorsExist(section.sectionName);

          if (isFieldRequired(field)) {
            handleRequiredFieldError(field, section);
          } else {
            handleFieldValid(field, section);
          }

          validateSpecialFieldTypes(field, section);
        });
      });
    resolve();
  });
};

function ensureSectionErrorsExist(sectionName) {
  if (!formErrors.value[sectionName]) {
    formErrors.value = {
      ...formErrors.value,
      [sectionName]: [],
    };
  }
}

function isFieldRequired(field) {
  return (
    field.properties?.IsRequired &&
    (field.value === "" || field.value === "[]" || field.value === "false" || field.value === false)
  );
}

function handleRequiredFieldError(field, section) {
  updateStepValidity(section, false);
  isInvalidStage = true;
  addFieldError(section.sectionName, field.fieldName);
}

function handleFieldValid(field, section) {
  removeFieldError(section.sectionName, field.fieldName);
  if (!isInvalidStage) {
    updateStepValidity(section, true);
  }
}

function updateStepValidity(section, isValid) {
  if (!section.properties.StageStep) return;

  const newSteps = [...steps.value];
  const stageStepText = section.properties.StageStep.text;
  const stepIndex = newSteps.findIndex((step) => step.label === stageStepText);

  if (stepIndex !== -1) {
    newSteps[stepIndex] = { ...newSteps[stepIndex], isValid };
    steps.value = newSteps;
  }
}

function addFieldError(sectionName, fieldName) {
  if (formErrors.value[sectionName]?.indexOf(fieldName) === -1 || !formErrors.value[sectionName]) {
    const newFormErrors = { ...formErrors.value };
    newFormErrors[sectionName] = newFormErrors[sectionName] || [];
    newFormErrors[sectionName].push(fieldName);
    formErrors.value = newFormErrors;
  }
}

function removeFieldError(sectionName, fieldName) {
  const newFormErrors = { ...formErrors.value };
  newFormErrors[sectionName] = newFormErrors[sectionName] || [];
  newFormErrors[sectionName] = newFormErrors[sectionName].filter((error) => error !== fieldName);
  formErrors.value = newFormErrors;
}

function validateSpecialFieldTypes(field, section) {
  if (field.fieldType === "Gallery") {
    validateGalleryField(field, section);
  }
  if (field.fieldType === "Table") {
    validateTableField(field, section);
  }
  if (field.fieldType === "ContinuousComments") {
    validateContinuousCommentsField(field, section);
  }
}

export const validateForm = () => {
  isInvalidStage = false;
  let visibilityResult = {};

  return new Promise((resolve) => {
    template.value.sections.forEach((section) => {
      visibilityResult = Invisible({
        VisibilityRules: section.properties?.VisibilityRules,
        parameterValues: section.properties?.ParameterValues,
      });

      if (visibilityResult.invisible) {
        const tempFormErrors = { ...formErrors.value };
        tempFormErrors[section.sectionName] = [];
        formErrors.value = tempFormErrors;
        return;
      }

      if (section.properties?.StageStep) {
        let disabledStage = steps.value?.some(
          (step) => step.text === section.properties.StageStep.text && step.disabled
        );

        if (disabledStage) {
          const tempFormErrors = { ...formErrors.value };
          tempFormErrors[section.sectionName] = [];
          formErrors.value = tempFormErrors;

          return;
        }

        const rules = template.value.properties?.VisibilityRules?.filter(
          (rule) => rule.stage === section.properties.StageStep.text
        );

        visibilityResult = {};
        let isInvisible = false;
        let isDisabled = false;

        if (rules) {
          visibilityResult = Invisible({
            VisibilityRules: rules,
            parameterValues: template.value.properties?.ParameterValues,
          });

          isInvisible = visibilityResult.invisible && !visibilityResult.disabled;
          isDisabled = visibilityResult.invisible && visibilityResult.disabled;
        }

        if (isInvisible) {
          const tempFormErrors = { ...formErrors.value };
          tempFormErrors[section.sectionName] = [];
          formErrors.value = tempFormErrors;

          return;
        }

        let stepIndex = steps.value?.findIndex(
          (item) => item.label === section.properties?.StageStep.text
        );
        if (!showAtlasMenu.value && stepIndex > 0) {
          isDisabled = true;
        }

        if (isDisabled) {
          const tempFormErrors = { ...formErrors.value };
          tempFormErrors[section.sectionName] = [];
          formErrors.value = tempFormErrors;

          return;
        }
      }

      section.fields.forEach((field) => {
        visibilityResult = Invisible({
          VisibilityRules: field.properties?.VisibilityRules,
          parameterValues: field.properties?.ParameterValues,
        });

        if (visibilityResult.invisible) return;

        ensureSectionErrorsExist(section.sectionName);

        if (isFieldRequired(field)) {
          handleRequiredFieldError(field, section);
        } else {
          handleFieldValidWithCleanup(field, section);
        }

        validateSpecialFieldTypes(field, section);
      });
    });
    resolve();
  });
};
function handleFieldValidWithCleanup(field, section) {
  removeFieldError(section.sectionName, field.fieldName);

  if (!isInvalidStage) {
    clearStepInvalidity(section);
  }
}

function clearStepInvalidity(section) {
  if (!section.properties.StageStep) return;

  const newSteps = [...steps.value];
  const stageStepText = section.properties.StageStep.text;
  const stepIndex = newSteps.findIndex(
    (step) => step.isValid === false && step.label === stageStepText
  );

  if (stepIndex !== -1) {
    delete newSteps[stepIndex].isValid;
    steps.value = newSteps;
  }
}
const validateContinuousCommentsField = (field, section) => {
  const currentComments = JSON.tryParse(field.value, []);
  const currentCommentsLength = currentComments.length;

  if (!field.properties?.IsRequired) {
    return;
  }

  const hasInitialValue = Object.hasOwn(initialCommentsLengths.value, field.fieldName);
  const initialCommentsLength = initialCommentsLengths.value[field.fieldName] ?? 0;

  const isInvalid = !hasInitialValue || currentCommentsLength <= initialCommentsLength;

  if (isInvalid && !formErrors.value?.[section.sectionName]?.includes(field.fieldName)) {
    const newFormErrors = { ...formErrors.value };
    newFormErrors[section.sectionName] = [...(newFormErrors[section.sectionName] || [])];

    if (!newFormErrors[section.sectionName].includes(field.fieldName)) {
      newFormErrors[section.sectionName].push(field.fieldName);
      formErrors.value = newFormErrors;
    }
  }
};

const validateGalleryField = (field, section) => {
  field.properties?.Fields?.forEach((galleryField) => {
    if (galleryField.properties?.IsRequired) {
      if (entryValues.value?.[field.fieldName] === "") {
        // Handle case where the gallery is an empty string
        formErrors.value = {
          ...formErrors.value,
          [section.sectionName]: [
            ...(formErrors.value[section.sectionName] || []),
            galleryField.fieldName + "_0",
          ],
        };
        return;
      }

      entryValues.value?.[field.fieldName]?.forEach((galleryValue, index) => {
        let visibilityResult = Invisible({
          VisibilityRules: galleryField.properties?.VisibilityRules,
          parameterValues: galleryField.properties?.ParameterValues,
          eventIndex: index,
          galleryName: field.fieldName,
        });

        if (visibilityResult.invisible) return;

        const errorKey = galleryField.fieldName + "_" + index;
        const isFieldEmpty = galleryValue[galleryField.fieldName] === "";

        if (isFieldEmpty) {
          handleGalleryFieldError(errorKey, section);
        } else {
          handleGalleryFieldValid(errorKey, section);
        }
      });
    }
  });
};

function handleGalleryFieldError(errorKey, section) {
  if (!formErrors.value?.[section.sectionName]?.includes(errorKey)) {
    formErrors.value = {
      ...formErrors.value,
      [section.sectionName]: [...(formErrors.value[section.sectionName] || []), errorKey],
    };
  }
  updateStepValidity(section, false);
  isInvalidStage = true;
}

function handleGalleryFieldValid(errorKey, section) {
  formErrors.value = {
    ...formErrors.value,
    [section.sectionName]:
      formErrors.value?.[section.sectionName]?.filter((item) => item !== errorKey) || [],
  };

  if (!isInvalidStage) {
    updateStepValidity(section, true);
  }
}

const validateTableField = (field, section) => {
  field.properties?.Columns?.forEach((column) => {
    if (Array.isArray(entryValues.value?.[field.fieldName])) {
      if (column.properties?.IsRequired) {
        entryValues.value?.[field.fieldName]?.forEach((row, index) => {
          let visibilityResult = Invisible({
            VisibilityRules: column.properties?.VisibilityRules,
            parameterValues: column.properties?.ParameterValues,
            eventIndex: index,
            galleryName: field.fieldName,
          });

          if (visibilityResult.invisible) {
            return;
          }
          const errorKey = column.columnName + "_" + index;
          const isFieldEmpty = row[column.columnName] === "";

          if (isFieldEmpty) {
            if (!formErrors.value?.[section.sectionName]?.includes(errorKey)) {
              formErrors.value = {
                ...formErrors.value,
                [section.sectionName]: [...(formErrors.value[section.sectionName] || []), errorKey],
              };
            }

            if (section.properties.StageStep) {
              const newSteps = [...steps.value];
              const stageStepText = section.properties.StageStep.text;
              const stepIndex = newSteps.findIndex((step) => step.label === stageStepText);

              if (stepIndex !== -1) {
                let newStep = { ...newSteps[stepIndex], isValid: false };
                newSteps[stepIndex] = newStep;
                steps.value = newSteps;

                isInvalidStage = true;
              }
            }
          } else if (!isInvalidStage) {
            if (section.properties.StageStep) {
              const newSteps = [...steps.value];
              const stageStepText = section.properties.StageStep.text;
              const stepIndex = newSteps.findIndex((step) => step.label === stageStepText);

              if (stepIndex !== -1) {
                let newStep = { ...newSteps[stepIndex], isValid: true };
                newSteps[stepIndex] = newStep;

                steps.value = newSteps;
              }
            }
          }
        });
      }
    }
  });
};

export function getEntryValues(entryData) {
  const entryValues = {};
  entryData?.sections?.forEach((section) => {
    section.fields.forEach((field) => {
      if (
        typeof field.properties?.Values === "object" &&
        !Array.isArray(field.properties?.Values)
      ) {
        entryValues[field.fieldName] = Object.values(field.properties?.Values);
      } else {
        entryValues[field.fieldName] = field.properties?.Values || field.value;
      }
    });
  });
  return entryValues;
}

export const getEntriesByTemplateId = (templateId) => {
  if (!templateId) return;

  let filteredEntries = userEntries.value?.[templateId]?.filter((entry) => {
    return entry.templateId === templateId;
  });

  if (!filteredEntries) return [];

  filteredEntries = filteredEntries.map((entry) => {
    const flattenedFields = {};

    entry.sections.forEach((section) => {
      section.fields.forEach((field) => {
        flattenedFields[field.fieldName] = field.value || field.values || field.properties.Values;
      });
    });

    const newEntry = { ...entry, ...flattenedFields };

    delete newEntry.sections;
    delete newEntry.templateId;
    delete newEntry.entryAuthor;
    delete newEntry.actionId;

    return newEntry;
  });

  filteredEntries.sort((a, b) => b.createdDate - a.createdDate);

  return filteredEntries;
};

export function Invisible({ VisibilityRules, parameterValues = {}, eventIndex, galleryName }) {
  if (!VisibilityRules?.length) return { invisible: false, disabled: false };

  let result = { invisible: false, disabled: false };

  for (const rule of VisibilityRules) {
    // Sanitize field names in the expression
    let sanitizedExpression = rule.expression.replaceAll("\r", "").replaceAll("\n", "");

    const values = entryValues.value;
    const parser = math.parser();

    if (typeof eventIndex === "number" && galleryName) {
      const galleryValues = values[galleryName] ?? [];
      const galleryDataRow = galleryValues?.[eventIndex] ?? {};
      Object.entries(galleryDataRow).forEach(([subKey, subValue]) => {
        const sanitizedSubKey = "_" + subKey.replace(/\s/g, "");
        const regex = new RegExp(`\\b${subKey.replace(/\s/g, "")}\\b`, "g");
        sanitizedExpression = sanitizedExpression.replace(regex, sanitizedSubKey);
        parser.set(sanitizedSubKey, subValue.toString());
      });
      Object.entries(values).forEach(([key, value]) => {
        if (!galleryDataRow[key]) {
          const sanitizedKey = "_" + key.replace(/\s/g, "");
          const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
          sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
          parser.set(sanitizedKey, value);
        }
      });
    } else if (typeof eventIndex !== "number") {
      Object.entries(values).forEach(([key, value]) => {
        const sanitizedKey = "_" + key.replace(/\s/g, "");
        const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
        sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
        parser.set(sanitizedKey, value);
      });
    } else {
      continue;
    }

    Object.entries(parameterValues ?? {}).forEach(([key, value]) => {
      const sanitizedKey = "_" + key.replace(/\s/g, "");
      const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
      sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
      parser.set(sanitizedKey, value);
    });

    try {
      let evaluation = parser.evaluate(sanitizedExpression);
      result.invisible = typeof evaluation === "boolean" ? evaluation : false;
      result.disabled = rule.disableIfTrue;
      if (rule.stopProcessing && result.invisible) {
        break;
      }
    } catch (error) {
      console.log("Error evaluating expression:", error);
      console.log("Expression:", sanitizedExpression);
      console.log("Rule:", rule);
      console.log("Context:", { galleryName, eventIndex });
      result.invisible = false;
    }
  }
  return result;
}

export const getConnectionData = ({ Data }) => {
  switch (Data?.type) {
    case "Template":
      return orderBy(filterBy(getEntriesByTemplateId(Data.id), Data.filter), Data.sort);
    case "Global Parameters":
    case "List":
      return orderBy(
        filterBy(allDatasets.value.find((dataset) => dataset.id === Data.id).values, Data.filter),
        Data.sort
      );
    default:
      return [];
  }
};

export const setInitialGalleryValues = ({
  galleryName,
  values,
  fields,
  field,
  callback = () => {},
}) => {
  if (!fields) {
    entryValues.value = { ...entryValues.value, [galleryName]: [] };
    galleryValues.value = { ...galleryValues.value, [galleryName]: [] };
    callback();
    return;
  }

  let newGalleryValues = values.map((value) => {
    const entryValue = {};
    fields.forEach((field) => {
      if (field?.fieldType === "Checkbox") {
        entryValue[field?.fieldName] = value[field?.valueMap] || "false";
        return;
      }

      if (field?.fieldType === "Time") {
        const newValue = new Date();
        const timeZoneOffset = newValue.getTimezoneOffset() * 60000;
        const adjustedDate = new Date(newValue.getTime() - timeZoneOffset)
          .toISOString()
          .replace("Z", "");

        entryValue[field.fieldName] = field.properties?.DefaultValue ? adjustedDate : null;
        return;
      }

      entryValue[field?.fieldName] = value[field.valueMap] || field?.properties?.DefaultValue || "";
    });
    return entryValue;
  });
  const exclusionFilterData = getConnectionData({ Data: field?.properties?.ExclusionFilter });

  if (field?.properties?.ExclusionFilter) {
    newGalleryValues = newGalleryValues.filter(
      (option) =>
        !exclusionFilterData.some(
          (exclusionFilterOption) =>
            exclusionFilterOption?.[field?.properties?.ExclusionFilterValue] ===
            option?.[field?.properties?.ExclusionFilterValue]
        )
    );
  }
  entryValues.value = {
    ...entryValues.value,
    [galleryName]: newGalleryValues,
  };

  galleryValues.value = {
    ...galleryValues.value,
    [galleryName]: newGalleryValues,
  };

  return newGalleryValues;
};

export async function getIndexFromTemplate({ templateId, indexName }) {
  //Fetch current index
  const templateData = await axios.get(
    `${process.env.REACT_APP_API_ENDPOINT}/Template/${templateId}`
  );
  let currentIndex = templateData.data?.properties?.IndexValues?.[indexName];
  if (currentIndex) {
    currentIndex = currentIndex + 1;
  } else {
    currentIndex = 1;
  }

  //Update new index back to template
  if (!templateData.data.properties?.IndexValues) {
    templateData.data.properties = {
      ...templateData.data.properties,
      IndexValues: { [indexName]: currentIndex },
    };
  }

  templateData.data.properties = {
    ...templateData.data.properties,
    IndexValues: {
      ...templateData.data.properties.IndexValues,
      [indexName]: currentIndex,
    },
  };
  await axios.put(
    `${process.env.REACT_APP_API_ENDPOINT}/Template/${templateId}?userId=${userEmail.value}`,
    templateData.data
  );

  const allTemplatesIndex = allTemplates.value.findIndex((t) => t.id === templateId);
  const allTemplatesCopy = [...allTemplates.value];
  allTemplatesCopy[allTemplatesIndex] = templateData.data;
  allTemplates.value = allTemplatesCopy;

  //Return updated index
  return currentIndex;
}

export const initializeSectionsEditEntry = (entryItem, getRevisionCount) => {
  return template.value?.sections?.map((section) => ({
    ...section,
    properties: section.properties || {},
    fields: section.fields.map((field) => {
      if (field.fieldType === "Gallery" || field.fieldType === "Table") {
        return {
          ...field,
          value: "",
          properties: {
            ...field.properties,
            Values: entryValues.value[field.fieldName] ?? [],
          },
        };
      }
      if (field.properties?.["RevisionCount"]) {
        return {
          ...field,
          value: entryValues.value[field.fieldName] ?? "",
          revisionCount: getRevisionCount(entryItem, field.fieldName),
        };
      }
      return {
        ...field,
        value: entryValues.value[field.fieldName] ?? "",
      };
    }),
  }));
};

export const initializeSectionsNewEntry = (sections, getRevisionCount) => {
  return sections?.map((section, sIndex) => ({
    ...section,
    properties: section.properties || {},
    fields: section.fields.map((field, fIndex) => {
      const fieldType = field?.fieldType;
      let fieldValue;

      if (fieldType === "BusinessUnit") {
        fieldValue = userData.value?.officeLocation;
      } else if (fieldType === "Checkbox") {
        fieldValue = "false";
      } else if (
        typeof field?.properties?.DefaultValue === "boolean" &&
        fieldType.includes("Date")
      ) {
        const newDate = new Date();
        if (field?.properties?.HoursOnly) {
          newDate.setMinutes(0);
          newDate.setSeconds(0);
          newDate.setMilliseconds(0);
        }
        const timezoneOffset = new Date().getTimezoneOffset() * 60000;
        fieldValue = field?.properties?.DefaultValue
          ? new Date(newDate.getTime() - timezoneOffset).toISOString().replace("Z", "")
          : "";
      } else {
        fieldValue = field.properties?.DefaultValue ?? "";
      }

      if (fieldType === "Gallery") {
        const initialGalleryValues = setInitialGalleryValues({
          galleryName: field?.fieldName,
          values: getConnectionData({ field, Data: field.properties?.Data }),
          fields: field?.properties?.Fields,
        });

        entryValues.value = {
          ...entryValues.value,
          [field?.fieldName]: initialGalleryValues,
        };

        const newTemplateView = JSON.tryParse(JSON.stringify(template.value));
        newTemplateView.sections[sIndex].fields[fIndex] = {
          ...newTemplateView.sections[sIndex].fields[fIndex],
          Values: initialGalleryValues,
        };
        template.value = newTemplateView;

        return {
          ...field,
          value: "",
          properties: {
            ...field?.properties,
            Values: initialGalleryValues,
          },
        };
      }

      if (fieldType === "Table") {
        entryValues.value = {
          ...entryValues.value,
          [field?.fieldName]: [],
        };

        const newTemplateView = JSON.tryParse(JSON.stringify(template.value));
        newTemplateView.sections[sIndex].fields[fIndex] = {
          ...newTemplateView.sections[sIndex].fields[fIndex],
          Values: [],
        };
        template.value = newTemplateView;

        return {
          ...field,
          value: "",
          properties: {
            ...field.properties,
            Values: [],
          },
        };
      }

      if (field.properties?.["RevisionCount"]) {
        return {
          ...field,
          value: fieldValue,
          revisionCount: getRevisionCount({ ...template.value }, field.fieldName),
        };
      }

      return {
        ...field,
        value: fieldValue,
      };
    }),
  }));
};

export const calculateExpression = (field, eventIndex) => {
  try {
    let sanitizedExpression = field.properties?.Expression.replaceAll("\r", "").replaceAll(
      "\n",
      ""
    );
    const values = entryValues.value;
    const parser = customMath.parser();

    parser.set("split", (str, separator) => str.split(separator));

    if (typeof eventIndex === "number" && field.properties?.galleryParentName) {
      const galleryValues = values[field.properties?.galleryParentName];
      if (!galleryValues?.[eventIndex]) {
        console.log("Gallery values or event index is undefined. Skipping evaluation.");
        return;
      }
      const galleryDataRow = galleryValues[eventIndex];
      Object.entries(galleryDataRow).forEach(([subKey, subValue]) => {
        const sanitizedSubKey = "_" + subKey.replace(/\s/g, "");
        const regex = new RegExp(`\\b${subKey.replace(/\s/g, "")}\\b`, "g");
        sanitizedExpression = sanitizedExpression.replace(regex, sanitizedSubKey);
        parser.set(sanitizedSubKey, subValue);
      });
    } else if (typeof eventIndex !== "number") {
      Object.entries(values).forEach(([key, value]) => {
        const sanitizedKey = "_" + key.replace(/\s/g, "");
        const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
        sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
        parser.set(sanitizedKey, value);
      });
    } else {
      return;
    }

    Object.entries(field?.properties?.ParameterValues ?? {}).forEach(([key, value]) => {
      const sanitizedKey = "_" + key.replace(/\s/g, "");
      const regex = new RegExp(`\\b${key.replace(/\s/g, "")}\\b`, "g");
      sanitizedExpression = sanitizedExpression.replace(regex, sanitizedKey);
      parser.set(sanitizedKey, value);
    });

    let result = parser.evaluate(sanitizedExpression) ?? "";
    if (Number.isNaN(result)) result = "";
    return result;
  } catch (error) {
    console.log(error);
    if (field.value !== "") {
      return "";
    }
  }
};

export function findChangedFields(initialValues, currentValues) {
  const updatedFields = new Set();
  const updatedTableDetails = {};
  const updatedGalleryDetails = {};

  const isDifferent = (a, b) => JSON.stringify(a) !== JSON.stringify(b);

  // Verify root fields
  for (const key in initialValues) {
    if (isDifferent(initialValues[key], currentValues[key])) {
      updatedFields.add(key);
    }
  }

  // Verify Tables and Galleries
  for (const fieldName in currentValues) {
    const templateField = findFieldInTemplate(fieldName);

    if (Array.isArray(currentValues[fieldName]) && currentValues[fieldName]) {
      const initialArray = initialValues[fieldName] || [];
      const currentArray = currentValues[fieldName];
      findTableChanges(
        templateField,
        currentArray,
        initialArray,
        isDifferent,
        updatedTableDetails,
        fieldName
      );
      findGalleryChanges(
        templateField,
        currentArray,
        initialArray,
        isDifferent,
        updatedGalleryDetails,
        fieldName
      );
    }
  }

  return {
    updatedRootFields: Array.from(updatedFields),
    updatedTableDetails,
    updatedGalleryDetails,
  };
}

const findTableChanges = (
  templateField,
  currentArray,
  initialArray,
  isDifferent,
  updatedTableDetails,
  fieldName
) => {
  if (templateField?.fieldType === "Table") {
    const changedRows = [];

    currentArray.forEach((currentRow) => {
      const initialRow = initialArray.find((r) => r.id === currentRow.id) || {};
      const changedColumns = [];

      for (const columnName in currentRow) {
        if (isDifferent(initialRow[columnName], currentRow[columnName])) {
          changedColumns.push(columnName);
        }
      }

      if (changedColumns.length > 0) {
        changedRows.push({
          rowId: currentRow.id,
          changedColumns,
        });
      }
    });

    if (changedRows.length > 0 || initialArray.length !== currentArray.length) {
      updatedTableDetails[fieldName] = {
        changedRows,
        structureChanged: initialArray.length !== currentArray.length,
      };
    }
  }
};

const findGalleryChanges = (
  templateField,
  currentArray,
  initialArray,
  isDifferent,
  updatedGalleryDetails,
  fieldName
) => {
  if (templateField?.fieldType === "Gallery") {
    const changedGalleryItems = [];

    currentArray.forEach((currentItem, index) => {
      const initialItem = initialArray[index] || {};
      const changedFields = [];

      const galleryFields = templateField.properties?.Fields || [];

      galleryFields.forEach((field) => {
        const fieldName = field.fieldName;
        if (isDifferent(initialItem[fieldName], currentItem[fieldName])) {
          changedFields.push(fieldName);
        }
      });

      if (changedFields.length > 0) {
        changedGalleryItems.push({
          itemIndex: index,
          changedFields,
        });
      }
    });

    if (changedGalleryItems.length > 0 || initialArray.length !== currentArray.length) {
      updatedGalleryDetails[fieldName] = {
        changedItems: changedGalleryItems,
        structureChanged: initialArray.length !== currentArray.length,
      };
    }
  }
};

export const findFieldInTemplate = (fieldName) => {
  for (const section of template.value.sections) {
    const field = section.fields.find((f) => f.fieldName === fieldName);
    if (field) return field;
  }
  return null;
};

export const addLastUpdateToGalleryFields = (field, galleryChanges, galleryValues) => {
  galleryChanges.changedItems.forEach((changedItem) => {
    const itemToUpdate = galleryValues?.[changedItem.itemIndex];

    if (itemToUpdate) {
      field.properties.Fields.forEach((galleryField) => {
        if (
          galleryField.properties?.TrackLastUpdate &&
          changedItem.changedFields.includes(galleryField.fieldName)
        ) {
          const date = new Date();
          const offset = date.getTimezoneOffset() * 60000;
          itemToUpdate[`${galleryField.fieldName}_LastUpdate`] = new Date(date.getTime() - offset)
            .toISOString()
            .replace("Z", "");

          if (galleryField.fieldType === "Button") {
            itemToUpdate[`${galleryField.fieldName}`] = "";
          }
        }
      });
    }
  });

  if (galleryChanges.structureChanged) {
    galleryValues?.forEach((item) => {
      field.properties.Fields.forEach((galleryField) => {
        if (
          galleryField.properties?.TrackLastUpdate &&
          !item[`${galleryField.fieldName}_LastUpdate`]
        ) {
          const date = new Date();
          const offset = date.getTimezoneOffset() * 60000;
          item[`${galleryField.fieldName}_LastUpdate`] = new Date(date.getTime() - offset)
            .toISOString()
            .replace("Z", "");
        }
      });
    });
  }
};

export const addLastUpdateToTableFields = (field, tableChanges, tableValues) => {
  tableChanges.changedRows.forEach((changedRow) => {
    const rowToUpdate = tableValues?.find((row) => row.id === changedRow.rowId);

    if (rowToUpdate) {
      field.properties.Columns.forEach((column) => {
        if (
          column.properties?.TrackLastUpdate &&
          changedRow.changedColumns.includes(column.columnName)
        ) {
          if (column.columnType === "Button" && rowToUpdate[`${column.columnName}`] === "") {
            return;
          }

          const date = new Date();
          const offset = date.getTimezoneOffset() * 60000;
          rowToUpdate[`${column.columnName}_LastUpdate`] = new Date(date.getTime() - offset)
            .toISOString()
            .replace("Z", "");

          if (column.columnType === "Button") {
            rowToUpdate[`${column.columnName}`] = "";
          }
        }
      });
    }
  });

  if (tableChanges.structureChanged) {
    tableValues?.forEach((row) => {
      field.properties.Columns.forEach((column) => {
        if (column.properties?.TrackLastUpdate && !row[`${column.columnName}_LastUpdate`]) {
          if (column.columnType === "Button" && row[`${column.columnName}`] === "") {
            return;
          }

          const date = new Date();
          const offset = date.getTimezoneOffset() * 60000;
          row[`${column.columnName}_LastUpdate`] = new Date(date.getTime() - offset)
            .toISOString()
            .replace("Z", "");
        }
      });
    });
  }
};

export const addLastUpdateToRootField = (field) => {
  const date = new Date();
  const offset = new Date().getTimezoneOffset() * 60000;
  field.properties.LastUpdate = new Date(date.getTime() - offset).toISOString().replace("Z", "");
};

export const rowRender = (event) => {
  // Check if row exists in resyncGalleryDifferences.values.additions
  const rowExists = resyncGalleryDifferences.value.additions.find(
    (addition) => JSON.stringify(addition) === JSON.stringify(event.dataItem)
  );

  if (rowExists) {
    const firstKey = Object.keys(event.dataItem)[0];
    // Clone the props.dataItem to avoid mutating the original data
    const updatedDataItem = { ...event.dataItem };
    updatedDataItem[firstKey] = "+ " + event.dataItem[firstKey];
    return <tr {...event.trProps}>{event.children}</tr>;
  }

  const trProps = { style: rowExists ? { backgroundColor: "#eaf5ea" } : {} };
  return <tr {...trProps} />;
};

export const getNextEnabledStep = (currentStep, steps) => {
  let nextStep = currentStep + 1;
  while (nextStep < steps.length && steps[nextStep].disabled) {
    nextStep++;
  }
  return nextStep < steps.length ? nextStep : currentStep;
};

export const getPreviousEnabledStep = (currentStep, steps) => {
  let previousStep = currentStep - 1;
  while (previousStep >= 0 && steps[previousStep].disabled) {
    previousStep--;
  }
  return previousStep >= 0 ? previousStep : currentStep;
};

export const copyToClipboard = () => {
  const currentUrl = window.location.href;
  let updatedUrl;
  const params = new URLSearchParams(window.location.search);

  if (!params.get("entryId")) {
    if (currentUrl.includes("entryOnly=")) {
      updatedUrl = currentUrl.replace(/(entryOnly=)[^&]+/, "$1true");
    } else {
      const separator = currentUrl.includes("?") ? "&" : "?";
      updatedUrl = `${currentUrl}${separator}entryOnly=true`;
    }
  } else {
    updatedUrl = currentUrl;
  }

  navigator.clipboard
    .writeText(updatedUrl)
    .then(() => {
      alert("Link copied to clipboard!");
    })
    .catch((err) => {
      console.error("error copying link: ", err);
    });
};

export const detectUnsavedChanges = (initialEntryValues, deepEqual) => {
  const currentValues = entryValues.value;
  for (const key in initialEntryValues) {
    if (!deepEqual(initialEntryValues[key], currentValues[key])) {
      return true;
    }
  }
  return false;
};

export const getRevisionCount = (obj, fieldName) => {
  for (const section of obj.sections) {
    for (const field of section.fields) {
      if (field.fieldName === fieldName) {
        return field.revisionCount ?? 0;
      }
    }
  }
  return "Rev # Not Found";
};

export const prepareFieldsForSubmission = (templateData, updatedFields) => {
  for (const section of templateData.sections) {
    const sectionResult = Invisible({
      VisibilityRules: section.properties.VisibilityRules,
      parameterValues: section.properties.ParameterValues,
      eventIndex: null,
      galleryName: "",
    });

    for (const field of section.fields) {
      processGalleriesAndTablesForSubmission(field);
      processContinuousCommentsForSubmission(field);
      processCalculatedFieldsForSubmission(sectionResult, field);
      processTableLastUpdate(field, updatedFields);
      processGalleryLastUpdate(field, updatedFields);
      processFieldsLastUpdates(field, updatedFields);
      processButtonLastUpdate(field);
    }
  }
};

const processGalleriesAndTablesForSubmission = (field) => {
  if (field.fieldType === "Gallery" || field.fieldType === "Table") {
    const updatedProperties = {
      ...field.properties,
      Values: entryValues.value[field.fieldName],
    };
    field.properties = updatedProperties;
  }
};

const processContinuousCommentsForSubmission = (field) => {
  if (field.fieldType === "ContinuousComments") {
    let comments = JSON.tryParse(field.value, []);

    comments.forEach((c) => {
      if (c.isTemporary) {
        delete c.isTemporary;
      }
    });

    field.value = JSON.stringify(comments);
  }
};

const processCalculatedFieldsForSubmission = (sectionResult, field) => {
  if (!sectionResult.invisible && field.fieldType === "Calculated") {
    const result = Invisible({
      VisibilityRules: field.properties?.VisibilityRules,
      parameterValues: field.properties?.ParameterValues,
      eventIndex: null,
      galleryName: field.properties?.galleryParentName,
    });
    if (!result.invisible) {
      field.value = calculateExpression(field, null);
    } else {
      field.value = "";
    }
  }
};

const processTableLastUpdate = (field, updatedFields) => {
  if (
    field.properties?.Columns &&
    updatedFields.updatedTableDetails[field.fieldName] &&
    field.fieldType === "Table"
  ) {
    const tableChanges = updatedFields.updatedTableDetails[field.fieldName];
    const tableValues = field.properties.Values;

    addLastUpdateToTableFields(field, tableChanges, tableValues);
  }
};

const processGalleryLastUpdate = (field, updatedFields) => {
  if (
    field.properties?.Fields &&
    updatedFields.updatedGalleryDetails[field.fieldName] &&
    field.fieldType === "Gallery"
  ) {
    const galleryChanges = updatedFields.updatedGalleryDetails[field.fieldName];
    const galleryValues = field.properties.Values;

    addLastUpdateToGalleryFields(field, galleryChanges, galleryValues);
  }
};

const processButtonLastUpdate = (field) => {
  if (field.properties?.TrackLastUpdate && field.fieldType === "Button") {
    field.properties.LastUpdate = field.value;
    field.value = "";
  }
};

const processFieldsLastUpdates = (field, updatedFields) => {
  if (
    field.properties?.TrackLastUpdate &&
    updatedFields.updatedRootFields.includes(field.fieldName) &&
    field.fieldType !== "Button"
  ) {
    addLastUpdateToRootField(field);
  }
};
